import React, { useState, useEffect, useRef } from 'react';
import {
  View, Text, FlatList, Image, StyleSheet, Modal, TextInput,
  TouchableOpacity, ScrollView, Alert, ActivityIndicator,
  useWindowDimensions, Switch,
} from 'react-native';
import {
  fetchCategories, createOrUpdateCategory, deleteCategory,
  addCategoryItem, updateCategoryItem, deleteCategoryItem,
  addSubCategory, updateSubCategory, deleteSubCategory,
  uploadCategoryImage, deleteCategoryStorageUrl,
  CategoryModel, CategoryItem, SubCategoryItem,
} from '../services/categoryService';

const C = {
  primary: '#21808D', primaryLight: '#e8f5f7',
  danger: '#ef4444', dangerLight: '#fee2e2',
  bg: '#0E1512', card: '#fff',
  textPrimary: '#1a2332', textMuted: '#64748b', border: '#e2e8f0',
};

// ─────────────────────────────────────────────────────────────────
export default function CategoryDashboard() {
  const { width } = useWindowDimensions();
  const isMobile = width < 700;
  const cols = width < 700 ? 1 : width < 1050 ? 2 : 3;
  const catImgRef = useRef<HTMLInputElement>(null);
  const bannerImgRef = useRef<HTMLInputElement>(null);
  const subImgRef = useRef<HTMLInputElement>(null);

  const [categories, setCategories] = useState<CategoryModel[]>([]);
  const [loading, setLoading] = useState(false);
  const [imgUploading, setImgUploading] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  // ── Category modal ──────────────────────────────────────────
  const [catModal, setCatModal] = useState(false);
  const [editCat, setEditCat] = useState<CategoryModel | null>(null);
  const [catTitle, setCatTitle] = useState('');
  const [catImage, setCatImage] = useState('');
  const [catColor, setCatColor] = useState('#21808D');
  const [catBanners, setCatBanners] = useState<string[]>([]);

  // ── Category-item modal ─────────────────────────────────────
  const [catItemModal, setCatItemModal] = useState(false);
  const [editCatItem, setEditCatItem] = useState<CategoryItem | null>(null);
  const [parentCat, setParentCat] = useState<CategoryModel | null>(null);
  const [catItemTitle, setCatItemTitle] = useState('');

  // ── Subcategory modal ───────────────────────────────────────
  const [subModal, setSubModal] = useState(false);
  const [editSub, setEditSub] = useState<SubCategoryItem | null>(null);
  const [subParentCat, setSubParentCat] = useState<CategoryModel | null>(null);
  const [subParentItem, setSubParentItem] = useState<CategoryItem | null>(null);
  const [subTitle, setSubTitle] = useState('');
  const [subImage, setSubImage] = useState('');

  // ── Load ────────────────────────────────────────────────────
  const load = async () => {
    setLoading(true);
    try { setCategories(await fetchCategories()); }
    catch { Alert.alert('Error', 'Failed to load categories'); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const toggleExpand = (id: string) =>
    setExpanded(prev => ({ ...prev, [id]: !prev[id] }));

  // ── Open category modal ─────────────────────────────────────
  const openCatModal = (cat?: CategoryModel) => {
    setEditCat(cat ?? null);
    setCatTitle(cat?.tittle ?? '');
    setCatImage(cat?.image ?? '');
    setCatColor(cat?.colorHex ?? '#21808D');
    setCatBanners(cat?.bannerImages ?? []);
    setCatModal(true);
  };

  const saveCat = async () => {
    if (!catTitle.trim()) { Alert.alert('Validation', 'Title is required'); return; }
    await createOrUpdateCategory({
      id: editCat?.id,
      tittle: catTitle,
      image: catImage,
      colorHex: catColor,
      bannerImages: catBanners,
      categoryItems: editCat?.categoryItems ?? [],
    });
    setCatModal(false);
    load();
  };

  const deleteCat = (cat: CategoryModel) => {
    Alert.alert('Delete Category', `Delete "${cat.tittle}"?`, [
      { text: 'Cancel', style: 'cancel' },
      {
        text: 'Delete', style: 'destructive', onPress: async () => {
          await deleteCategory(cat.id);
          if (cat.image) await deleteCategoryStorageUrl(cat.image);
          for (const b of cat.bannerImages ?? []) await deleteCategoryStorageUrl(b);
          load();
        }
      },
    ]);
  };

  // ── Category image upload ───────────────────────────────────
  const handleCatImg = async (e: any) => {
    const file: File = e.target.files?.[0];
    if (!file) return;
    setImgUploading('catImg');
    try {
      const id = editCat?.id ?? `cat_${Date.now()}`;
      const url = await uploadCategoryImage(file, id, 'main');
      setCatImage(url);
    } catch { Alert.alert('Error', 'Upload failed'); }
    setImgUploading(null);
    e.target.value = '';
  };

  const handleBannerImg = async (e: any) => {
    const files: FileList = e.target.files;
    if (!files?.length) return;
    setImgUploading('banner');
    try {
      const id = editCat?.id ?? `cat_${Date.now()}`;
      const urls: string[] = [];
      for (let i = 0; i < files.length; i++) {
        const url = await uploadCategoryImage(files[i], id, `banner_${Date.now()}_${i}`);
        urls.push(url);
      }
      setCatBanners(prev => [...prev, ...urls]);
    } catch { Alert.alert('Error', 'Upload failed'); }
    setImgUploading(null);
    e.target.value = '';
  };

  const removeBanner = async (url: string) => {
    setCatBanners(prev => prev.filter(u => u !== url));
    await deleteCategoryStorageUrl(url);
  };

  // ── Category item modal ─────────────────────────────────────
  const openCatItemModal = (cat: CategoryModel, item?: CategoryItem) => {
    setParentCat(cat);
    setEditCatItem(item ?? null);
    setCatItemTitle(item?.tittle ?? '');
    setCatItemModal(true);
  };

  const saveCatItem = async () => {
    if (!parentCat || !catItemTitle.trim()) return;
    if (editCatItem) {
      await updateCategoryItem(parentCat.id, editCatItem.id, { tittle: catItemTitle });
    } else {
      await addCategoryItem(parentCat.id, {
        id: Date.now().toString(), tittle: catItemTitle, subCategoryItems: [],
      });
    }
    setCatItemModal(false);
    load();
  };

  const deleteCatItem = (cat: CategoryModel, item: CategoryItem) => {
    Alert.alert('Delete', `Delete item "${item.tittle}"?`, [
      { text: 'Cancel', style: 'cancel' },
      { text: 'Delete', style: 'destructive', onPress: async () => {
        await deleteCategoryItem(cat.id, item.id);
        load();
      }},
    ]);
  };

  // ── Subcategory modal ───────────────────────────────────────
  const openSubModal = (cat: CategoryModel, item: CategoryItem, sub?: SubCategoryItem) => {
    setSubParentCat(cat);
    setSubParentItem(item);
    setEditSub(sub ?? null);
    setSubTitle(sub?.tittle ?? '');
    setSubImage(sub?.image ?? '');
    setSubModal(true);
  };

  const handleSubImg = async (e: any) => {
    const file: File = e.target.files?.[0];
    if (!file) return;
    setImgUploading('subImg');
    try {
      const id = subParentCat?.id ?? `cat_${Date.now()}`;
      const url = await uploadCategoryImage(file, id, `sub_${Date.now()}`);
      setSubImage(url);
    } catch { Alert.alert('Error', 'Upload failed'); }
    setImgUploading(null);
    e.target.value = '';
  };

  const saveSub = async () => {
    if (!subParentCat || !subParentItem || !subTitle.trim()) return;
    if (editSub) {
      await updateSubCategory(subParentCat.id, subParentItem.id, editSub.id, {
        tittle: subTitle, image: subImage,
      });
    } else {
      await addSubCategory(subParentCat.id, subParentItem.id, {
        id: Date.now().toString(), tittle: subTitle, image: subImage,
      });
    }
    setSubModal(false);
    load();
  };

  const deleteSub = (cat: CategoryModel, item: CategoryItem, sub: SubCategoryItem) => {
    Alert.alert('Delete', `Delete "${sub.tittle}"?`, [
      { text: 'Cancel', style: 'cancel' },
      { text: 'Delete', style: 'destructive', onPress: async () => {
        await deleteSubCategory(cat.id, item.id, sub.id);
        if (sub.image) await deleteCategoryStorageUrl(sub.image);
        load();
      }},
    ]);
  };

  // ── Render subcategory chip ─────────────────────────────────
  const renderSub = (cat: CategoryModel, item: CategoryItem, sub: SubCategoryItem) => (
    <View key={sub.id} style={styles.subChip}>
      {sub.image
        ? <Image source={{ uri: sub.image }} style={styles.subChipImg} />
        : <View style={[styles.subChipImg, styles.subChipImgPlaceholder]}><Text style={{ fontSize: 16 }}>🏷️</Text></View>
      }
      <Text style={styles.subChipText} numberOfLines={1}>{sub.tittle}</Text>
      <View style={styles.subChipActions}>
        <TouchableOpacity onPress={() => openSubModal(cat, item, sub)} style={styles.subChipEdit}>
          <Text style={styles.subChipEditText}>✎</Text>
        </TouchableOpacity>
        <TouchableOpacity onPress={() => deleteSub(cat, item, sub)} style={styles.subChipDel}>
          <Text style={styles.subChipDelText}>✕</Text>
        </TouchableOpacity>
      </View>
    </View>
  );

  // ── Render category item ────────────────────────────────────
  const renderCatItem = (cat: CategoryModel, item: CategoryItem) => (
    <View key={item.id} style={styles.catItem}>
      <View style={styles.catItemHeader}>
        <Text style={styles.catItemTitle}>{item.tittle}</Text>
        <View style={styles.catItemActions}>
          <TouchableOpacity style={styles.actionBtnSmall} onPress={() => openCatItemModal(cat, item)}>
            <Text style={styles.actionBtnSmallText}>Edit</Text>
          </TouchableOpacity>
          <TouchableOpacity style={[styles.actionBtnSmall, { backgroundColor: C.dangerLight }]} onPress={() => deleteCatItem(cat, item)}>
            <Text style={[styles.actionBtnSmallText, { color: C.danger }]}>Delete</Text>
          </TouchableOpacity>
          <TouchableOpacity style={[styles.actionBtnSmall, { backgroundColor: '#ede9fe' }]} onPress={() => openSubModal(cat, item)}>
            <Text style={[styles.actionBtnSmallText, { color: '#7c3aed' }]}>+ Sub</Text>
          </TouchableOpacity>
        </View>
      </View>
      <View style={styles.subChipsRow}>
        {(item.subCategoryItems ?? []).map(sub => renderSub(cat, item, sub))}
        {(item.subCategoryItems ?? []).length === 0 && (
          <Text style={styles.noSubText}>No subcategories yet</Text>
        )}
      </View>
    </View>
  );

  // ── Render category card ────────────────────────────────────
  const renderCategory = ({ item }: { item: CategoryModel }) => {
    const isExpanded = !!expanded[item.id];
    const accent = item.colorHex || C.primary;
    return (
      <View style={[styles.catCard, { borderLeftColor: accent, borderLeftWidth: 4 }]}>
        {/* Card header */}
        <View style={styles.catCardHeader}>
          {item.image
            ? <Image source={{ uri: item.image }} style={styles.catImage} resizeMode="cover" />
            : (
              <View style={[styles.catImage, styles.catImagePlaceholder, { backgroundColor: accent + '22' }]}>
                <Text style={{ fontSize: 28 }}>🗂️</Text>
              </View>
            )
          }
          <View style={{ flex: 1 }}>
            <Text style={styles.catTitle}>{item.tittle}</Text>
            <View style={styles.catMeta}>
              <View style={[styles.colorDot, { backgroundColor: accent }]} />
              <Text style={styles.catMetaText}>{accent}</Text>
              <View style={styles.catMetaChip}>
                <Text style={styles.catMetaChipText}>{(item.categoryItems ?? []).length} items</Text>
              </View>
            </View>
          </View>
          <View style={styles.catHeaderActions}>
            <TouchableOpacity style={styles.catEditBtn} onPress={() => openCatModal(item)}>
              <Text style={styles.catEditBtnText}>Edit</Text>
            </TouchableOpacity>
            <TouchableOpacity style={styles.catDelBtn} onPress={() => deleteCat(item)}>
              <Text style={styles.catDelBtnText}>Delete</Text>
            </TouchableOpacity>
          </View>
        </View>

        {/* Banner strip */}
        {(item.bannerImages ?? []).length > 0 && (
          <ScrollView horizontal showsHorizontalScrollIndicator={false} style={styles.bannerStrip}>
            {item.bannerImages.map((uri, i) => (
              <Image key={i} source={{ uri }} style={styles.bannerImg} resizeMode="cover" />
            ))}
          </ScrollView>
        )}

        {/* Expand toggle */}
        <TouchableOpacity style={styles.expandBtn} onPress={() => toggleExpand(item.id)}>
          <Text style={styles.expandBtnText}>
            {isExpanded ? '▲ Hide Items' : `▼ Show Items (${(item.categoryItems ?? []).length})`}
          </Text>
        </TouchableOpacity>

        {/* Category items (expanded) */}
        {isExpanded && (
          <View style={styles.catItemsWrap}>
            {(item.categoryItems ?? []).map(ci => renderCatItem(item, ci))}
            <TouchableOpacity style={styles.addItemBtn} onPress={() => openCatItemModal(item)}>
              <Text style={styles.addItemText}>+ Add Category Item</Text>
            </TouchableOpacity>
          </View>
        )}
      </View>
    );
  };

  // ─────────────────────────────────────────────────────────────
  return (
    <View style={styles.container}>
      {/* Hidden file inputs */}
      <input type="file" accept="image/*" ref={catImgRef as any} style={{ display: 'none' }} onChange={handleCatImg} />
      <input type="file" accept="image/*" multiple ref={bannerImgRef as any} style={{ display: 'none' }} onChange={handleBannerImg} />
      <input type="file" accept="image/*" ref={subImgRef as any} style={{ display: 'none' }} onChange={handleSubImg} />

      {/* Page header */}
      <View style={styles.pageHeader}>
        <span className="material-icons-round" style={{ fontSize: 20, color: '#4ECDC4', lineHeight: 1, flexShrink: 0 }}>category</span>
        <View style={{ flex: 1 }}>
          <Text style={styles.pageTitle}>Categories</Text>
          <Text style={styles.pageSubtitle}>{categories.length} categories</Text>
        </View>
        <TouchableOpacity style={styles.addCatBtn} onPress={() => openCatModal()}>
          <Text style={styles.addCatText}>+ New Category</Text>
        </TouchableOpacity>
      </View>

      {loading ? (
        <View style={styles.loader}>
          <ActivityIndicator size="large" color={C.primary} />
        </View>
      ) : categories.length === 0 ? (
        <View style={styles.empty}>
          <Text style={styles.emptyIcon}>🗂️</Text>
          <Text style={styles.emptyTitle}>No categories yet</Text>
        </View>
      ) : (
        <FlatList
          data={categories}
          keyExtractor={c => c.id}
          renderItem={renderCategory}
          numColumns={cols}
          key={String(cols)}
          columnWrapperStyle={cols > 1 ? { gap: 14, alignItems: 'flex-start' } : undefined}
          contentContainerStyle={{ paddingBottom: 60 }}
        />
      )}

      {/* ── Category Modal ─────────────────────────────────────── */}
      <Modal visible={catModal} animationType="slide" onRequestClose={() => setCatModal(false)}>
        <View style={ms.screen}>
          <View style={ms.header}>
            <TouchableOpacity onPress={() => setCatModal(false)}>
              <Text style={ms.backText}>✕ Cancel</Text>
            </TouchableOpacity>
            <Text style={ms.title}>{editCat ? 'Edit Category' : 'New Category'}</Text>
            <TouchableOpacity style={ms.saveBtn} onPress={saveCat}>
              <Text style={ms.saveBtnText}>Save</Text>
            </TouchableOpacity>
          </View>
          <ScrollView contentContainerStyle={ms.scroll}>
            {/* Image section */}
            <Text style={ms.section}>Category Image</Text>
            <View style={ms.imageRow}>
              {catImage
                ? <Image source={{ uri: catImage }} style={ms.catImg} resizeMode="cover" />
                : <View style={[ms.catImg, ms.imgPlaceholder]}><Text style={{ fontSize: 36 }}>🗂️</Text></View>
              }
              <View style={ms.imgBtns}>
                <TouchableOpacity style={ms.uploadBtn} onPress={() => catImgRef.current?.click()} disabled={imgUploading === 'catImg'}>
                  {imgUploading === 'catImg'
                    ? <ActivityIndicator color="#fff" size="small" />
                    : <Text style={ms.uploadBtnText}>{catImage ? 'Change' : 'Upload'}</Text>
                  }
                </TouchableOpacity>
                {catImage
                  ? <TouchableOpacity style={ms.removeBtn} onPress={() => setCatImage('')}>
                      <Text style={ms.removeBtnText}>Remove</Text>
                    </TouchableOpacity>
                  : null
                }
              </View>
            </View>

            <Text style={ms.section}>Details</Text>
            <Text style={ms.label}>Title *</Text>
            <TextInput style={ms.input} value={catTitle} onChangeText={setCatTitle} placeholder="Category title" placeholderTextColor="#94a3b8" />

            <Text style={ms.label}>Color</Text>
            <View style={ms.colorRow}>
              <input
                type="color"
                value={catColor}
                onChange={(e: any) => setCatColor(e.target.value)}
                style={{ width: 46, height: 46, border: 'none', padding: 0, cursor: 'pointer', borderRadius: 10, marginRight: 12 }}
              />
              <TextInput
                style={[ms.input, { flex: 1, marginBottom: 0 }]}
                value={catColor}
                onChangeText={setCatColor}
                placeholder="#21808D"
                placeholderTextColor="#94a3b8"
              />
            </View>

            <Text style={ms.section}>Banner Images</Text>
            <TouchableOpacity style={ms.addBannerBtn} onPress={() => bannerImgRef.current?.click()} disabled={imgUploading === 'banner'}>
              {imgUploading === 'banner'
                ? <ActivityIndicator color={C.primary} size="small" />
                : <Text style={ms.addBannerText}>+ Upload Banners</Text>
              }
            </TouchableOpacity>
            {catBanners.length > 0 && (
              <ScrollView horizontal showsHorizontalScrollIndicator={false} style={{ marginTop: 12 }}>
                {catBanners.map((uri, i) => (
                  <View key={i} style={{ marginRight: 10, position: 'relative' }}>
                    <Image source={{ uri }} style={ms.bannerThumb} resizeMode="cover" />
                    <TouchableOpacity style={ms.bannerDel} onPress={() => removeBanner(uri)}>
                      <Text style={{ color: '#fff', fontSize: 10, fontWeight: '700' }}>✕</Text>
                    </TouchableOpacity>
                  </View>
                ))}
              </ScrollView>
            )}
          </ScrollView>
        </View>
      </Modal>

      {/* ── Category Item Modal ──────────────────────────────────── */}
      <Modal visible={catItemModal} transparent animationType="fade" onRequestClose={() => setCatItemModal(false)}>
        <View style={ms.overlay}>
          <View style={ms.dialog}>
            <Text style={ms.dialogTitle}>{editCatItem ? 'Edit Item' : 'New Category Item'}</Text>
            <Text style={ms.label}>Title *</Text>
            <TextInput style={ms.input} value={catItemTitle} onChangeText={setCatItemTitle} placeholder="Item title" placeholderTextColor="#94a3b8" autoFocus />
            <View style={ms.dialogBtns}>
              <TouchableOpacity style={ms.dialogCancel} onPress={() => setCatItemModal(false)}>
                <Text style={ms.dialogCancelText}>Cancel</Text>
              </TouchableOpacity>
              <TouchableOpacity style={ms.dialogSave} onPress={saveCatItem}>
                <Text style={ms.dialogSaveText}>Save</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>

      {/* ── Subcategory Modal ──────────────────────────────────── */}
      <Modal visible={subModal} transparent animationType="fade" onRequestClose={() => setSubModal(false)}>
        <View style={ms.overlay}>
          <View style={ms.dialog}>
            <Text style={ms.dialogTitle}>{editSub ? 'Edit Subcategory' : 'New Subcategory'}</Text>

            {/* Sub image */}
            <View style={ms.subImgRow}>
              {subImage
                ? <Image source={{ uri: subImage }} style={ms.subImg} resizeMode="cover" />
                : <View style={[ms.subImg, ms.imgPlaceholder]}><Text style={{ fontSize: 24 }}>🏷️</Text></View>
              }
              <TouchableOpacity style={[ms.uploadBtn, { flex: 1 }]} onPress={() => subImgRef.current?.click()} disabled={imgUploading === 'subImg'}>
                {imgUploading === 'subImg'
                  ? <ActivityIndicator color="#fff" size="small" />
                  : <Text style={ms.uploadBtnText}>{subImage ? 'Change' : 'Upload Image'}</Text>
                }
              </TouchableOpacity>
            </View>

            <Text style={ms.label}>Title *</Text>
            <TextInput style={ms.input} value={subTitle} onChangeText={setSubTitle} placeholder="Subcategory title" placeholderTextColor="#94a3b8" />
            <View style={ms.dialogBtns}>
              <TouchableOpacity style={ms.dialogCancel} onPress={() => setSubModal(false)}>
                <Text style={ms.dialogCancelText}>Cancel</Text>
              </TouchableOpacity>
              <TouchableOpacity style={ms.dialogSave} onPress={saveSub}>
                <Text style={ms.dialogSaveText}>Save</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>
    </View>
  );
}

// ── Main styles ───────────────────────────────────────────────────
const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: '#0E1512', padding: 16 },
  pageHeader: { flexDirection: 'row', alignItems: 'center', gap: 10, backgroundColor: '#0A1712', marginTop: -16, marginHorizontal: -16, marginBottom: 20, paddingHorizontal: 16, paddingVertical: 12, borderBottomWidth: 1, borderBottomColor: 'rgba(33,128,141,0.18)' },
  pageTitle: { fontSize: 15, fontWeight: '800', color: '#fff' },
  pageSubtitle: { fontSize: 11, color: '#8BAAB8', marginTop: 1 },
  addCatBtn: { backgroundColor: '#21808D', paddingHorizontal: 20, paddingVertical: 11, borderRadius: 12 },
  addCatText: { color: '#fff', fontWeight: '700', fontSize: 14 },
  loader: { flex: 1, justifyContent: 'center', alignItems: 'center', paddingTop: 60 },
  empty: { flex: 1, justifyContent: 'center', alignItems: 'center', paddingTop: 80 },
  emptyIcon: { fontSize: 56, marginBottom: 12 },
  emptyTitle: { fontSize: 18, fontWeight: '600', color: '#E2EDE6' },

  catCard: {
    flex: 1, backgroundColor: '#17201B', borderRadius: 16, marginBottom: 14,
    shadowColor: '#000', shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.07, shadowRadius: 8, elevation: 3, overflow: 'hidden',
  },
  catCardHeader: { flexDirection: 'row', alignItems: 'center', gap: 14, padding: 16 },
  catImage: { width: 60, height: 60, borderRadius: 12, backgroundColor: '#f1f5f9' },
  catImagePlaceholder: { justifyContent: 'center', alignItems: 'center' },
  catTitle: { fontSize: 17, fontWeight: '700', color: '#E2EDE6', marginBottom: 4 },
  catMeta: { flexDirection: 'row', alignItems: 'center', gap: 8 },
  colorDot: { width: 14, height: 14, borderRadius: 7 },
  catMetaText: { fontSize: 11, color: '#7A9A80' },
  catMetaChip: { backgroundColor: '#e8f5f7', borderRadius: 6, paddingHorizontal: 8, paddingVertical: 2 },
  catMetaChipText: { fontSize: 10, color: '#21808D', fontWeight: '600' },
  catHeaderActions: { gap: 6 },
  catEditBtn: { backgroundColor: '#e8f5f7', borderRadius: 8, paddingHorizontal: 12, paddingVertical: 6 },
  catEditBtnText: { color: '#21808D', fontWeight: '700', fontSize: 12 },
  catDelBtn: { backgroundColor: '#fee2e2', borderRadius: 8, paddingHorizontal: 12, paddingVertical: 6 },
  catDelBtnText: { color: '#ef4444', fontWeight: '700', fontSize: 12 },

  bannerStrip: { paddingHorizontal: 16, paddingBottom: 12 },
  bannerImg: { width: 280, height: 130, borderRadius: 10, marginRight: 10, backgroundColor: '#f1f5f9' },

  expandBtn: { marginHorizontal: 16, marginBottom: 12, backgroundColor: '#0E1512', borderRadius: 10, paddingVertical: 10, alignItems: 'center', borderWidth: 1, borderColor: 'rgba(255,255,255,0.09)' },
  expandBtnText: { color: '#21808D', fontWeight: '600', fontSize: 13 },

  catItemsWrap: { paddingHorizontal: 16, paddingBottom: 16 },
  catItem: { backgroundColor: '#0E1512', borderRadius: 12, padding: 14, marginBottom: 10, borderWidth: 1, borderColor: 'rgba(255,255,255,0.09)' },
  catItemHeader: { flexDirection: 'row', alignItems: 'center', marginBottom: 10 },
  catItemTitle: { flex: 1, fontSize: 14, fontWeight: '700', color: '#E2EDE6' },
  catItemActions: { flexDirection: 'row', gap: 6 },
  actionBtnSmall: { backgroundColor: '#e8f5f7', borderRadius: 7, paddingHorizontal: 10, paddingVertical: 5 },
  actionBtnSmallText: { color: '#21808D', fontWeight: '600', fontSize: 11 },

  subChipsRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 8 },
  subChip: { backgroundColor: '#17201B', borderRadius: 12, borderWidth: 1, borderColor: 'rgba(255,255,255,0.09)', padding: 8, alignItems: 'center', minWidth: 80, maxWidth: 120 },
  subChipImg: { width: 44, height: 44, borderRadius: 22, marginBottom: 6, backgroundColor: '#f1f5f9' },
  subChipImgPlaceholder: { justifyContent: 'center', alignItems: 'center' },
  subChipText: { fontSize: 11, color: '#E2EDE6', fontWeight: '500', textAlign: 'center', marginBottom: 6 },
  subChipActions: { flexDirection: 'row', gap: 4 },
  subChipEdit: { backgroundColor: '#e8f5f7', borderRadius: 5, paddingHorizontal: 6, paddingVertical: 3 },
  subChipEditText: { color: '#21808D', fontSize: 11, fontWeight: '700' },
  subChipDel: { backgroundColor: '#fee2e2', borderRadius: 5, paddingHorizontal: 6, paddingVertical: 3 },
  subChipDelText: { color: '#ef4444', fontSize: 11, fontWeight: '700' },
  noSubText: { fontSize: 12, color: '#94a3b8', fontStyle: 'italic' },

  addItemBtn: { backgroundColor: '#21808D', borderRadius: 10, paddingVertical: 10, paddingHorizontal: 16, alignSelf: 'flex-start', marginTop: 8 },
  addItemText: { color: '#fff', fontWeight: '700', fontSize: 13 },
});

// ── Modal styles ──────────────────────────────────────────────────
const ms = StyleSheet.create({
  screen: { flex: 1, backgroundColor: '#17201B' },
  header: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', paddingHorizontal: 20, paddingVertical: 16, borderBottomWidth: 1, borderBottomColor: '#e2e8f0' },
  backText: { color: '#7A9A80', fontSize: 15, fontWeight: '500' },
  title: { fontSize: 17, fontWeight: '700', color: '#E2EDE6' },
  saveBtn: { backgroundColor: '#21808D', paddingHorizontal: 18, paddingVertical: 8, borderRadius: 8 },
  saveBtnText: { color: '#fff', fontWeight: '700', fontSize: 14 },
  scroll: { padding: 20, paddingBottom: 40 },
  section: { fontSize: 13, fontWeight: '700', color: '#21808D', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 12, marginTop: 16 },
  label: { fontSize: 13, fontWeight: '600', color: '#A8C4AF', marginBottom: 6 },
  input: { borderWidth: 1, borderColor: 'rgba(255,255,255,0.09)', borderRadius: 10, paddingHorizontal: 14, paddingVertical: 11, fontSize: 14, color: '#E2EDE6', backgroundColor: '#fafafa', marginBottom: 14 },

  imageRow: { flexDirection: 'row', gap: 16, alignItems: 'center', marginBottom: 8 },
  catImg: { width: 100, height: 100, borderRadius: 12, backgroundColor: '#f1f5f9' },
  imgPlaceholder: { justifyContent: 'center', alignItems: 'center', borderWidth: 2, borderColor: 'rgba(255,255,255,0.09)' },
  imgBtns: { gap: 10 },
  uploadBtn: { backgroundColor: '#21808D', paddingHorizontal: 18, paddingVertical: 10, borderRadius: 10, alignItems: 'center' },
  uploadBtnText: { color: '#fff', fontWeight: '600', fontSize: 13 },
  removeBtn: { backgroundColor: '#fee2e2', paddingHorizontal: 14, paddingVertical: 8, borderRadius: 10, alignItems: 'center' },
  removeBtnText: { color: '#ef4444', fontWeight: '600', fontSize: 12 },

  colorRow: { flexDirection: 'row', alignItems: 'center', marginBottom: 14 },

  addBannerBtn: { borderWidth: 2, borderColor: '#21808D', borderRadius: 10, paddingVertical: 12, alignItems: 'center' },
  addBannerText: { color: '#21808D', fontWeight: '600', fontSize: 14 },
  bannerThumb: { width: 140, height: 80, borderRadius: 8, backgroundColor: '#f1f5f9' },
  bannerDel: { position: 'absolute', top: 4, right: 4, backgroundColor: 'rgba(0,0,0,0.6)', width: 18, height: 18, borderRadius: 9, justifyContent: 'center', alignItems: 'center' },

  overlay: { flex: 1, backgroundColor: 'rgba(0,0,0,0.5)', justifyContent: 'center', paddingHorizontal: 24 },
  dialog: { backgroundColor: '#17201B', borderRadius: 20, padding: 24, shadowColor: '#000', shadowOffset: { width: 0, height: 6 }, shadowOpacity: 0.15, shadowRadius: 16, elevation: 8 },
  dialogTitle: { fontSize: 18, fontWeight: '700', color: '#E2EDE6', marginBottom: 16 },
  dialogBtns: { flexDirection: 'row', gap: 12, marginTop: 8 },
  dialogCancel: { flex: 1, paddingVertical: 12, borderRadius: 10, backgroundColor: '#0E1512', alignItems: 'center', borderWidth: 1, borderColor: 'rgba(255,255,255,0.09)' },
  dialogCancelText: { color: '#7A9A80', fontWeight: '600', fontSize: 14 },
  dialogSave: { flex: 1, paddingVertical: 12, borderRadius: 10, backgroundColor: '#21808D', alignItems: 'center' },
  dialogSaveText: { color: '#fff', fontWeight: '700', fontSize: 14 },

  subImgRow: { flexDirection: 'row', alignItems: 'center', gap: 12, marginBottom: 14 },
  subImg: { width: 64, height: 64, borderRadius: 32, backgroundColor: '#f1f5f9' },
});
